import { useCallback, useLayoutEffect, useRef } from 'react';

import { createMutation } from '../mutation/createMutation';
import { KeyNamespaces } from '../utils/getKey';
import type {
    CreateEntityMutationOptions,
    QueryParamsBaseType,
    UseCreateEntity,
} from './createPaginationEntityTypes';
import type { CacheKey } from '../utils/internalTypings';
import type {
    MutationImperativeHelpersReturn,
    MutationState,
} from '../mutation/createMutationTypes';
import { getQueryClient } from '../queryClientSingleton';
import {
    invalidateListEntities,
    updateEntityCache,
    updateEntityListCache,
} from './createPaginationEntityCacheHelpers';

type CreateMutationPayload<Payload, QueryPageParams extends QueryParamsBaseType> = {
    payload: Payload;
    listCacheKeyParams?: QueryPageParams;
};

type CreateMutationHelpers<
    Entity,
    CreatePayload,
    QueryPageParams extends QueryParamsBaseType,
> = Omit<
    MutationImperativeHelpersReturn<Entity, CreateMutationPayload<CreatePayload, QueryPageParams>>,
    'mutate' | 'selectData' | 'selectSlice'
> & {
    mutate: (
        payload: CreatePayload,
        options?: CreateEntityMutationOptions<QueryPageParams>,
    ) => Promise<Entity>;
    selectData: (arg?: CreatePayload) => Entity | undefined;
    selectSlice: (arg?: CreatePayload) => MutationState<Entity>;
};

function getMutationCreateFn<Entity, Payload, QueryPageParams extends QueryParamsBaseType>(
    {
        fetch,
        name,
        selectId,
        localListsSynchronisation,
    }: {
        fetch: (payload: Payload) => Promise<Entity>;
        name: string;
        selectId: CacheKey<Entity>;
        localListsSynchronisation: boolean | undefined;
    },
    client = getQueryClient(),
) {
    return async ({
        payload,
        listCacheKeyParams,
    }: CreateMutationPayload<Payload, QueryPageParams>) => {
        const entity = await fetch(payload);

        updateEntityCache(
            entity,
            selectId(entity),
            {
                name,
            },
            client,
        );

        if (localListsSynchronisation) {
            updateEntityListCache([entity], {
                name,
                selectId,
                operation: localListsSynchronisation ? 'APPEND' : undefined,
                params: listCacheKeyParams,
            });
        } else {
            // results in invalidating lists with auto refetch of active ones
            invalidateListEntities({ name, params: listCacheKeyParams }, client);
        }

        return entity;
    };
}

export const createPaginationEntityMutationCreate = <
    Entity,
    CreatePayload,
    QueryPageParams extends QueryParamsBaseType = QueryParamsBaseType,
>({
    localListsSynchronisation,
    create,
    name,
    selectId,
}: {
    name: string;
    localListsSynchronisation: boolean | undefined;
    create?: (payload: CreatePayload) => Promise<Entity>;
    selectId: CacheKey<Entity>;
}): [
    UseCreateEntity<Entity, CreatePayload, QueryPageParams>,
    CreateMutationHelpers<Entity, CreatePayload, QueryPageParams>,
] => {
    const createFetch = getMutationCreateFn({
        name,
        fetch: (payload: CreatePayload) => {
            if (!create) {
                throw new Error('createPaginationEntity: create is not defined');
            }
            return create(payload);
        },
        selectId,
        localListsSynchronisation,
    });
    const [useCreateMutation, mutationHelpers] = createMutation({
        name: `${KeyNamespaces.PAGINATION_ENTITY}/${name}/detail/create`,
        payloadCreator: createFetch,
    });
    const useCreateMutationWrapper: UseCreateEntity<Entity, CreatePayload, QueryPageParams> = (
        arg,
    ) => {
        const wrappedArg = arg === undefined ? undefined : { payload: arg };
        const result = useCreateMutation(wrappedArg);

        const latestRef = useRef({ arg, mutate: result.mutate });
        useLayoutEffect(() => {
            latestRef.current = { arg, mutate: result.mutate };
        });

        const createWithOptions = useCallback(
            (
                optionalHookArgOverride?: CreatePayload,
                options?: CreateEntityMutationOptions<QueryPageParams>,
            ) => {
                const { arg: latestArg, mutate } = latestRef.current;
                return mutate({
                    payload: (optionalHookArgOverride ?? latestArg) as CreatePayload,
                    listCacheKeyParams: options?.listCacheKeyParams,
                });
            },
            [],
        );

        return {
            data: result.data,
            create: createWithOptions,
            error: result.error,
            mutate: createWithOptions,
            reset: result.reset,
            isPending: result.isPending,
        };
    };
    return [
        useCreateMutationWrapper,
        {
            mutate: (payload, options) => {
                return mutationHelpers.mutate({
                    payload,
                    listCacheKeyParams: options?.listCacheKeyParams,
                });
            },
            selectData: (arg) => {
                return mutationHelpers.selectData(arg === undefined ? undefined : { payload: arg });
            },
            selectSlice: (arg) => {
                return mutationHelpers.selectSlice(
                    arg === undefined ? undefined : { payload: arg },
                );
            },
        },
    ];
};
